import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';

import { InfoTooltip, FormField, LoadingSpinner, SearchField } from '@shared/components';
import ConfirmationDialog from '@shared/components/dialogs/ConfirmationDialog';
import { NoDataFound } from '@shared/components/NoDataFound/NoDataFound';
import { useFormDialog, useConfirmDialog } from '@shared/hooks/useDialog';
import { usePlaybookFields, useSavePlaybookField, useDeletePlaybookField } from '../hooks/usePlaybook';
import type { PlaybookSection, DynamicFieldConfig, DynamicFieldType } from '../types';
import { usePermission } from '@modules/auth/hooks';
import { Module } from '@modules/admin/types';

interface Props {
  section: PlaybookSection;
}

const fieldTypes: { label: string; value: DynamicFieldType }[] = [
  { label: 'Text', value: 'text' },
  { label: 'Number', value: 'number' },
  // { label: 'JSON', value: 'json' },
  { label: 'Select', value: 'select' },
  { label: 'Multi Select', value: 'multiselect' },
  { label: 'Boolean', value: 'boolean' },
  { label: 'Date', value: 'date' },
];

const DynamicFieldsTab: React.FC<Props> = ({ section: _section }) => {
  const { canAdd, canUpdate } = usePermission(Module.PLAYBOOK);

  const moduleType = _section.toLowerCase();
  const { data: localItems = [], isLoading: loading } = usePlaybookFields(moduleType);
  const saveFieldMutation = useSavePlaybookField(moduleType);
  const deleteFieldMutation = useDeletePlaybookField(moduleType);

  const [searchQuery, setSearchQuery] = useState('');

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return localItems;
    const query = searchQuery.toLowerCase();
    return localItems.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.key.toLowerCase().includes(query) ||
        item.type.toLowerCase().includes(query),
    );
  }, [localItems, searchQuery]);

  // Form dialog for add/edit field
  const formDialog = useFormDialog<Omit<DynamicFieldConfig, 'id'>>({
    key: '',
    label: '',
    type: 'text',
    required: false,
    tooltip: '',
    options: [],
  });

  // Confirm dialog for delete
  const deleteDialog = useConfirmDialog<DynamicFieldConfig>();

  const startAdd = () => {
    formDialog.handleOpen();
  };

  const startEdit = (it: DynamicFieldConfig) => {
    formDialog.handleOpen(it);
  };

  const submit = async () => {
    if (!formDialog.form.key || !formDialog.form.label) return;

    try {
      // Create a copy of form data without options for non-select field types
      const { options, ...formDataWithoutOptions } = formDialog.form;

      // Only include options for select and multiselect field types
      const fieldToSaveData =
        formDialog.form.type === 'select' || formDialog.form.type === 'multiselect'
          ? formDialog.form
          : formDataWithoutOptions;

      let fieldToSave = fieldToSaveData;
      if (formDialog.isEditing && formDialog.editingItem) {
        fieldToSave = { fieldId: formDialog.editingItem.fieldId, ...fieldToSaveData };
      }

      await saveFieldMutation.mutateAsync(fieldToSave);
      formDialog.handleClose();
    } catch (error) {
      // Error handling is done by the mutation
    }
  };

  const [optionInput, setOptionInput] = useState({ label: '', value: '' });
  const addOption = () => {
    if (!optionInput.label || !optionInput.value) return;
    formDialog.updateForm({
      options: [...(formDialog.form.options || []), { label: optionInput.label, value: optionInput.value }],
    });
    setOptionInput({ label: '', value: '' });
  };
  const removeOption = (value: string) => {
    formDialog.updateForm({ options: (formDialog.form.options || []).filter((o) => o.value !== value) });
  };

  const handleDeleteClick = (field: DynamicFieldConfig) => {
    deleteDialog.handleOpen(field);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.data?.fieldId) return;

    try {
      await deleteFieldMutation.mutateAsync(String(deleteDialog.data.fieldId));
      deleteDialog.handleClose();
    } catch (error) {
      // Error handling is done by the mutation
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">Dynamic Fields</Typography>
          <InfoTooltip
            title={
              <>
                Define additional fields displayed in forms for the selected section. Supports text, select, boolean,
                date, and more.
              </>
            }
          />
        </Stack>
        {canAdd && (
          <Stack direction="row" alignItems="center" justifyContent="center">
            <SearchField value={searchQuery} onChange={setSearchQuery} placeholder="Search by label, key, or type..." />
            <Button startIcon={<Add />} variant="contained" onClick={startAdd} size="small" sx={{ ml: 1 }}>
              Add Field
            </Button>
            <InfoTooltip title="Create a new field for this section. You can choose type, mark as required, and add options." />
          </Stack>
        )}
      </Stack>

      {/* Search Bar */}

      {loading && <LoadingSpinner message="Loading fields..." />}

      {localItems.length === 0 && !loading && (
        <NoDataFound message='No fields defined yet. Click "Add Field" to create one.' py={8} />
      )}

      {filteredItems.length === 0 && localItems.length > 0 && (
        <NoDataFound message="No fields match your search." py={4} />
      )}

      {filteredItems.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 650 }} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Label</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Key</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Required</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((it) => (
                <TableRow key={it.key} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{it.label}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {it.key}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={it.type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell>{it.required ? 'True' : 'False'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {canUpdate && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => startEdit(it)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDeleteClick(it)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={formDialog.open} onClose={formDialog.handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography component="span" variant="h6" fontSize={18}>
              {formDialog.isEditing ? 'Edit Field' : 'Add Field'}
            </Typography>
            <InfoTooltip title="Fields appear in creation/edit forms. Use a stable key; users see the label." />
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            <Box>
              <FormField
                label="Key"
                tooltip="Machine-readable unique key (e.g., internal_notes). Cannot collide with existing keys."
              />
              <TextField
                placeholder="Key"
                value={formDialog.form.key}
                onChange={(e) => formDialog.updateForm({ key: e.target.value })}
                fullWidth
              />
            </Box>
            <Box>
              <FormField label="Label" tooltip="User-friendly label displayed in forms." />
              <TextField
                placeholder="Label"
                value={formDialog.form.label}
                onChange={(e) => formDialog.updateForm({ label: e.target.value })}
                fullWidth
              />
            </Box>
            <Box>
              <FormField label="Description" tooltip="Description shown as a tooltip icon next to the field label." />
              <TextField
                placeholder="Enter a helpful description for this field..."
                value={formDialog.form.tooltip || ''}
                onChange={(e) => formDialog.updateForm({ tooltip: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
            </Box>
            <Box>
              <FormField label="Type" tooltip="Determines how the field is rendered and validated." />
              <TextField
                select
                placeholder="Type"
                value={formDialog.form.type}
                onChange={(e) => formDialog.updateForm({ type: e.target.value as DynamicFieldType })}
                fullWidth
              >
                {fieldTypes.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box>
              <FormField label="Required" tooltip="If enabled, users must provide a value for this field." />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formDialog.form.required}
                    onChange={(e) => formDialog.updateForm({ required: e.target.checked })}
                  />
                }
                label=""
              />
            </Box>
            {(formDialog.form.type === 'select' || formDialog.form.type === 'multiselect') && (
              <Box>
                <FormField
                  label="Options"
                  tooltip="Add selectable options as label/value pairs. Applicable for select and multi-select types."
                />
                <Stack gap={1}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                    <TextField
                      placeholder="Label"
                      value={optionInput.label}
                      onChange={(e) => setOptionInput((o) => ({ ...o, label: e.target.value }))}
                    />
                    <TextField
                      placeholder="Value"
                      value={optionInput.value}
                      onChange={(e) => setOptionInput((o) => ({ ...o, value: e.target.value }))}
                    />
                    <Button onClick={addOption}>Add</Button>
                  </Stack>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {(formDialog.form.options || []).map((o) => (
                      <Chip key={o.value} label={`${o.label} (${o.value})`} onDelete={() => removeOption(o.value)} />
                    ))}
                  </Stack>
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={formDialog.handleClose}>Cancel</Button>
          <Button onClick={submit} variant="contained" disabled={!formDialog.form.key || !formDialog.form.label}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Field"
        message={`Are you sure you want to delete the field "${deleteDialog.data?.label}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="error"
        confirmColor="error"
      />
    </Box>
  );
};

export default DynamicFieldsTab;
